import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  DndContext, PointerSensor, useSensor, useSensors, DragOverlay,
  useDraggable, useDroppable, closestCenter,
} from "@dnd-kit/core";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Sparkle, DotsThreeVertical, MapPin } from "@phosphor-icons/react";

const STAGES = [
  { id: "new", label: "New", tone: "bg-blue-500" },
  { id: "contacted", label: "Contacted", tone: "bg-cyan-500" },
  { id: "proposal", label: "Proposal", tone: "bg-amber-500" },
  { id: "negotiation", label: "Negotiation", tone: "bg-orange-500" },
  { id: "won", label: "Won", tone: "bg-emerald-500" },
  { id: "lost", label: "Lost", tone: "bg-rose-500" },
];

function fmt(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

function formatError(error) {
  const detail = error?.response?.data?.detail;
  if (!detail) return "Import failed.";
  if (typeof detail === "string") return detail;
  return JSON.stringify(detail);
}

function LeadCard({ lead, dragging }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: lead.id });
  const style = transform ? { transform: `translate3d(${transform.x}px,${transform.y}px,0)`, zIndex: 50 } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      data-testid={`lead-card-${lead.id}`}
      className={`group rounded-lg border border-border bg-card p-3.5 cursor-grab active:cursor-grabbing hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-150 ${dragging ? "opacity-50" : ""}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{lead.name}</div>
          <div className="text-xs text-muted-foreground truncate">{lead.company}</div>
        </div>
        <button className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          <DotsThreeVertical size={14} />
        </button>
      </div>

      <div className="flex items-center justify-between mt-3">
        <span className="text-sm font-mono">{fmt(lead.value)}</span>
        <div className="flex items-center gap-1.5">
          <span className="overline">AI</span>
          <span className="text-xs font-medium text-primary">{lead.ai_score ?? "-"}</span>
        </div>
      </div>

      <div className="mt-2 h-1 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${lead.probability || 0}%` }} />
      </div>
    </div>
  );
}

function Column({ stage, leads, onAdd }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  const total = leads.reduce((s, l) => s + (l.value || 0), 0);

  return (
    <div className="w-[300px] shrink-0 flex flex-col">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${stage.tone}`}></span>
          <span className="text-sm font-medium tracking-wide">{stage.label}</span>
          <span className="text-xs text-muted-foreground">- {leads.length}</span>
        </div>
        <span className="text-xs text-muted-foreground font-mono">{fmt(total)}</span>
      </div>

      <div
        ref={setNodeRef}
        data-testid={`lead-column-${stage.id}`}
        className={`flex-1 rounded-xl border border-dashed p-2 space-y-2 transition-colors min-h-[400px] ${isOver ? "border-primary/60 bg-primary/5" : "border-border/60 bg-card/40"}`}
      >
        {leads.map((l) => <LeadCard key={l.id} lead={l} />)}
        <button onClick={() => onAdd(stage.id)} className="w-full text-xs py-2 text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1" data-testid={`add-lead-${stage.id}`}>
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}

const EMPTY = {
  name: "",
  company: "",
  email: "",
  value: 0,
  probability: 20,
  source: "Outbound",
  stage: "new",
};

const FINDER_EMPTY = {
  query: "",
  location: "",
  max_results: 20,
  enrich_emails: true,
};

export default function Leads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [activeId, setActiveId] = useState(null);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const [finderOpen, setFinderOpen] = useState(false);
  const [finderForm, setFinderForm] = useState(FINDER_EMPTY);
  const [finderLoading, setFinderLoading] = useState(false);
  const [finderError, setFinderError] = useState("");

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };

  useEffect(() => { load(); }, []);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s.id, []]));
    for (const l of leads) (map[l.stage] || map.new).push(l);
    return map;
  }, [leads]);

  const onDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const lead = leads.find((l) => l.id === active.id);
    if (!lead || lead.stage === over.id) return;

    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, stage: over.id } : l)));

    try {
      await api.patch(`/leads/${lead.id}/stage`, { stage: over.id });
    } catch {
      load();
    }
  };

  const openAdd = (stage) => {
    setForm({ ...EMPTY, stage });
    setOpen(true);
  };

  const submitFinder = async () => {
    if (!finderForm.query || !finderForm.location) return;

    setFinderLoading(true);
    setFinderError("");

    try {
      await api.post("/google-places/import-leads", {
        query: finderForm.query,
        location: finderForm.location,
        max_results: parseInt(finderForm.max_results || 20, 10),
        enrich_emails: finderForm.enrich_emails,
        include_email_discovery: finderForm.enrich_emails,
      });

      setFinderOpen(false);
      setFinderForm(FINDER_EMPTY);
      navigate("/customers?newHotels=1");
    } catch (error) {
      setFinderError(formatError(error));
    } finally {
      setFinderLoading(false);
    }
  };

  const submit = async () => {
    if (!form.name) return;

    await api.post("/leads", {
      ...form,
      value: parseFloat(form.value || 0),
      probability: parseInt(form.probability || 0, 10),
    });

    setOpen(false);
    setForm(EMPTY);
    load();
  };

  const active = leads.find((l) => l.id === activeId);

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="overline mb-2">Pipeline</div>
          <h1 className="font-display text-4xl tracking-tight leading-none">Leads</h1>
          <p className="text-muted-foreground text-sm mt-2">Drag to move deals across stages. AI scores update in real time.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setFinderOpen(true)} variant="outline" className="rounded-full" data-testid="business-finder-btn">
            <MapPin size={14} className="mr-1.5" /> Bedrijvenzoeker
          </Button>

          <Button onClick={() => openAdd("new")} className="rounded-full bg-primary text-primary-foreground" data-testid="new-lead-btn">
            <Plus size={14} className="mr-1.5" /> New lead
          </Button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={onDragEnd}>
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {STAGES.map((s) => <Column key={s.id} stage={s} leads={byStage[s.id]} onAdd={openAdd} />)}
          </div>
        </div>
        <DragOverlay>
          {active ? <LeadCard lead={active} dragging /> : null}
        </DragOverlay>
      </DndContext>

      <Dialog open={finderOpen} onOpenChange={setFinderOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Bedrijvenzoeker</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="space-y-1.5">
              <Label className="overline">Zoekterm *</Label>
              <Input
                value={finderForm.query}
                onChange={(e) => setFinderForm({ ...finderForm, query: e.target.value })}
                placeholder="hotel, restaurant, tandarts"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="overline">Locatie *</Label>
              <Input
                value={finderForm.location}
                onChange={(e) => setFinderForm({ ...finderForm, location: e.target.value })}
                placeholder="Alkmaar, Utrecht, Amsterdam"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="overline">Aantal</Label>
              <Input
                type="number"
                min="1"
                max="60"
                value={finderForm.max_results}
                onChange={(e) => setFinderForm({ ...finderForm, max_results: e.target.value })}
              />
            </div>

            <label className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
              <span>Publieke e-mails zoeken en valideren</span>
              <input
                type="checkbox"
                checked={finderForm.enrich_emails}
                onChange={(e) => setFinderForm({ ...finderForm, enrich_emails: e.target.checked })}
              />
            </label>

            {finderError ? (
              <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-sm text-red-500">
                {finderError}
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setFinderOpen(false)}>Annuleren</Button>
            <Button onClick={submitFinder} disabled={finderLoading}>
              <MapPin size={14} className="mr-1.5" />
              {finderLoading ? "Importeren..." : "Naar Customers importeren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">New lead</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5">
              <Label className="overline">Name *</Label>
              <Input data-testid="lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>

            <div className="col-span-2 space-y-1.5">
              <Label className="overline">Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label className="overline">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label className="overline">Source</Label>
              <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label className="overline">Value EUR</Label>
              <Input type="number" data-testid="lead-value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>

            <div className="space-y-1.5">
              <Label className="overline">Probability %</Label>
              <Input type="number" value={form.probability} onChange={(e) => setForm({ ...form, probability: e.target.value })} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="lead-save">
              <Sparkle size={14} className="mr-1.5" /> Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
